import { EmployeeRequest } from '../pages/user/userTypes';
import { emptyValidationErrors, FieldError, ValidationErrors } from '../api/restApi';
import { QuestionnaireRequest } from '../pages/questionnaire/questionnaireTypes';
import moment from 'moment';
import { translation } from '../i18n';
import { QuestionCatalogueRequest } from '../pages/questionCatalogue/qustionCatalogueTypes';
import { QuestionModuleRequest } from '../pages/questionModuleTypes';
import { QuestionClusterRequest } from '../pages/questionClusterTypes';
import { ChoiceRequest } from '../pages/choiceTypes';
import { QuestionRequest, QuestionType } from '../pages/questionTypes';
import { ColumnRequest } from '../pages/columnTypes';

export function isValidEmail(email?: string): boolean {
  if (!email || email === null) {
    return false;
  }
  // From https://www.regular-expressions.info/email.html with modifications to allow some special characters
  return /^[A-Z0-9._%+ÄäÖöÜüß-]+@[A-Z0-9.ÄäÖöÜüß-]+\.[A-Z]{2,}$/i.test(email)
}

export function isEmpty(input?: string): boolean {
  if (!input || input === null || input === '') {
    return true;
  }
  return input.length <= 0;
}

export function isValid(employee?: EmployeeRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyFirstName: FieldError = {field: 'firstName', code: 'NotEmpty', defaultMessage: 'darf nicht leer sein'};
  const emptyLastName: FieldError = {field: 'lastName', code: 'NotEmpty', defaultMessage: 'darf nicht leer sein'};
  const emptyEmail: FieldError = {field: 'email', code: 'NotEmpty', defaultMessage: 'darf nicht leer sein'};
  const invalidMail: FieldError = {field: 'email', code: 'Email', defaultMessage: 'keine gültige E-Mail-Adresse'};
  if (!employee || employee === null) {
    fieldErrors.push(emptyFirstName);
    fieldErrors.push(emptyLastName);
    fieldErrors.push(emptyEmail);
    fieldErrors.push(invalidMail);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (isEmpty(employee.firstName)) {
    fieldErrors.push(emptyFirstName);
  }

  if (isEmpty(employee.lastName)) {
    fieldErrors.push(emptyLastName);
  }

  if (isEmpty(employee.email)) {
    fieldErrors.push(emptyEmail);
  }

  if (!isValidEmail(employee.email)) {
    fieldErrors.push(invalidMail);
  }
  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidQuestionnaire(questionnaire: QuestionnaireRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyTitle: FieldError = {field: 'title', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.title')};
  const emptyStartDate: FieldError = {field: 'startDate', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.startDate')};
  const emptyEndDate: FieldError = {field: 'endDate', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.endDate')};
  const emptyAppraisee: FieldError = {field: 'appraisee', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.appraisee')};
  const emptyAppraisers: FieldError = {field: 'appraisers', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.appraisers')};
  const emptyCatalogue: FieldError = {field: 'catalogue', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.catalogue')};
  const appraiseeAsAppraiser: FieldError = {field: 'appraisers', code: 'Conflict', defaultMessage: 'conflict'};
  const endDateBeforeStartDate: FieldError = {
    field: 'endDate',
    code: 'Conflict',
    defaultMessage: 'Enddatum darf nicht vor dem Startdatum liegen'
  };
  const startDateAfterEndDate: FieldError = {
    field: 'startDate',
    code: 'Conflict',
    defaultMessage: 'Startdatm darf nicht nach dem Enddatum liegen'
  };

  if (!questionnaire || questionnaire === null) {
    fieldErrors.push(emptyTitle);
    fieldErrors.push(emptyStartDate);
    fieldErrors.push(emptyEndDate);
    fieldErrors.push(emptyAppraisee);
    fieldErrors.push(emptyAppraisers);
    fieldErrors.push(emptyCatalogue);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (isEmpty(questionnaire.title)) {
    fieldErrors.push(emptyTitle);
  }

  if (isEmpty(questionnaire.startDate)) {
    fieldErrors.push(emptyStartDate);
  }

  if (isEmpty(questionnaire.endDate)) {
    fieldErrors.push(emptyEndDate);
  }

  if (!questionnaire.catalogue || questionnaire.catalogue === undefined || questionnaire.catalogue === null || questionnaire.catalogue.id === -1) {
    fieldErrors.push(emptyCatalogue);
  }

  if (!questionnaire.appraisee || questionnaire.appraisee === undefined || questionnaire.appraisee === null || questionnaire.appraisee.id === '') {
    fieldErrors.push(emptyAppraisee);
  }

  if (!questionnaire.appraisers || questionnaire.appraisers === undefined || questionnaire.appraisers === null || questionnaire.appraisers.length < 1) {
    fieldErrors.push(emptyAppraisers);
  }

  if (questionnaire.appraisers.indexOf(questionnaire.appraisee) !== -1) {
    fieldErrors.push(appraiseeAsAppraiser);
  }

  if (moment(questionnaire.startDate).isAfter(moment(questionnaire.endDate))) {
    fieldErrors.push(endDateBeforeStartDate);
    fieldErrors.push(startDateAfterEndDate);
  }

  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidQuestionCatalogue(catalogue: QuestionCatalogueRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyTitle: FieldError = {field: 'title', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.title')};
  const emptyModule: FieldError = {field: 'module', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.module')};

  if (!catalogue || catalogue === null) {
    fieldErrors.push(emptyTitle);
    fieldErrors.push(emptyModule);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (isEmpty(catalogue.title)) {
    fieldErrors.push(emptyTitle);
  }

  if (catalogue.modules.length === 0) {
    fieldErrors.push(emptyModule);
  }
  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidQuestionModule(module: QuestionModuleRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyModule: FieldError = {field: 'cluster', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.cluster')};

  if (!module || module === null) {
    fieldErrors.push(emptyModule);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (module.clusters.length === 0) {
    fieldErrors.push(emptyModule);
  }
  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidQuestionCluster(cluster: QuestionClusterRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyQuestions: FieldError = {field: 'question', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.questions')};

  if (!cluster || cluster === null) {
    fieldErrors.push(emptyQuestions);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (cluster.questions.length === 0) {
    fieldErrors.push(emptyQuestions);
  }
  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidChoice(choice: ChoiceRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyDescription: FieldError = {field: 'description', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.description')};

  if (!choice || choice === null) {
    fieldErrors.push(emptyDescription);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (isEmpty(choice.description)) {
    fieldErrors.push(emptyDescription);
  }
  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidColumn(column: ColumnRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyTitle: FieldError = {field: 'title', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.title')};

  if (!column || column === null) {
    fieldErrors.push(emptyTitle);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (isEmpty(column.title)) {
    fieldErrors.push(emptyTitle);
  }
  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidTextQuestion(question: QuestionRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyQuestion: FieldError = {field: 'question', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.question')};
  const invalidQuestionType: FieldError = {field: 'questionTypeText', code: 'Conflict', defaultMessage: translation.t('validationErrors.NotEmpty.question')};

  if (!question || question === null) {
    fieldErrors.push(emptyQuestion);
    fieldErrors.push(invalidQuestionType);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (isEmpty(question.question)) {
    fieldErrors.push(emptyQuestion);
  }

  if (question.questionType !== QuestionType.TEXT) {
    fieldErrors.push(invalidQuestionType);
  }

  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidMultipleChoiceQuestion(question: QuestionRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyChoice: FieldError = {field: 'choices', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.choices')};
  const invalidQuestionType: FieldError = {field: 'questionTypeMultipleChoice', code: 'Conflict', defaultMessage: translation.t('validationErrors.Conflict.questionTypeMultipleChoice')};

  if (!question || question === null) {
    fieldErrors.push(emptyChoice);
    fieldErrors.push(invalidQuestionType);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (question.choices === undefined || question.choices.length < 2) {
    fieldErrors.push(emptyChoice);
  }

  if (question.questionType !== QuestionType.MULTIPLE_CHOICE) {
    fieldErrors.push(invalidQuestionType);
  }

  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}

export function isValidMultipleColumnQuestion(question: QuestionRequest): ValidationErrors {
  const validationErrors = emptyValidationErrors();
  const fieldErrors: FieldError[] = [];
  const emptyColumn: FieldError = {field: 'columns', code: 'NotEmpty', defaultMessage: translation.t('validationErrors.NotEmpty.columns')};
  const invalidQuestionType: FieldError = {field: 'questionTypeMultipleChoice', code: 'Conflict', defaultMessage: translation.t('validationErrors.Conflict.questionTypeMultipleColumn')};

  if (!question || question === null) {
    fieldErrors.push(emptyColumn);
    fieldErrors.push(invalidQuestionType);

    validationErrors.fieldErrors = fieldErrors;
    return validationErrors;
  }

  if (question.columns === undefined || question.columns.length < 2) {
    fieldErrors.push(emptyColumn);
  }

  if (question.questionType !== QuestionType.MULTIPLE_COLUMNS) {
    fieldErrors.push(invalidQuestionType);
  }

  validationErrors.fieldErrors = fieldErrors;

  return validationErrors;
}
